"use client";

import React, { useEffect, useRef, useState } from "react";
import { useInView } from "framer-motion";
import { Link as RouterLink } from "react-router-dom";
import { useLanguage } from "../../contexts/LanguageContext";
import { Band, SectionHead } from "../ui/Section";
import Reveal from "../ui/Reveal";
import Icon from "../ui/Icon";
import VideoFrame from "../ui/VideoFrame";

const DEMO_VIDEO = "2uXh8F1hwso";
const PARTNER_EMAIL = "mailto:elie.bubuya@hyphentech.africa";

/* ---------- shared pieces --------------------------------------------- */

const StatRow = ({ items }) => (
  <div className="grid grid-cols-2 border-t border-line lg:grid-cols-4">
    {items.map((stat, index) => (
      <Reveal
        key={stat.label}
        delay={index * 0.05}
        className={[
          "py-6 lg:py-7",
          index % 2 === 1 ? "border-l border-line pl-5" : "",
          index >= 2 ? "border-t border-line lg:border-t-0" : "",
          index > 0 ? "lg:border-l lg:pl-6" : "",
        ].join(" ")}
      >
        <p className="num text-[30px] leading-none md:text-[36px]">{stat.value}</p>
        <p className="mt-3 text-[14px] font-semibold">{stat.label}</p>
        {stat.desc ? <p className="meta mt-1.5 max-w-[30ch] text-[13px]">{stat.desc}</p> : null}
      </Reveal>
    ))}
  </div>
);

const FlowLine = ({ text }) => (
  <p className="num inline-flex flex-wrap items-center gap-x-2 rounded-md border border-line bg-surface px-4 py-3 text-[13px] text-ink-2">
    {text}
  </p>
);

const AwardCard = ({ ft }) => (
  <Reveal className="panel flex flex-col gap-6 p-6 sm:flex-row sm:items-center sm:gap-8 md:p-8">
    <div className="flex h-14 w-14 flex-none items-center justify-center rounded-md border border-line bg-clay-soft text-clay">
      <Icon name="trophy" size={24} />
    </div>

    <div className="min-w-0 flex-1">
      <div className="flex flex-wrap items-center gap-2.5">
        <span className="tag tag--next">{ft.award.badge}</span>
        <span className="eyebrow">{ft.award.competition}</span>
      </div>
      <h3 className="mt-3 text-[19px] font-semibold tracking-[-0.01em]">
        {ft.award.achievement}
      </h3>
      <p className="meta mt-2 max-w-[54ch]">{ft.award.description}</p>
    </div>

    <div className="flex-none border-t border-line pt-5 sm:border-l sm:border-t-0 sm:pl-8 sm:pt-0">
      <p className="num text-[30px] leading-none">{ft.award.prize}</p>
      <p className="eyebrow mt-2">{ft.award.prizeLabel}</p>
    </div>
  </Reveal>
);

/* ---------- condensed band for the home page --------------------------- */

export const FintechBand = () => {
  const { t } = useLanguage();
  const ft = t.fintech;

  const stats = [
    { value: ft.problem.stat1Value, label: ft.problem.stat1Label },
    { value: ft.problem.stat2Value, label: ft.problem.stat2Label },
    { value: ft.problem.stat3Value, label: ft.problem.stat3Label },
    { value: ft.problem.stat4Value, label: ft.problem.stat4Label },
  ];

  return (
    <Band id="fintech">
      <SectionHead
        index={4}
        label={t.nav.fintech}
        title={ft.hero.title}
        action={
          <RouterLink to="/fintech" className="btn btn--secondary">
            {ft.whatIs.title}
            <Icon name="arrowRight" size={16} />
          </RouterLink>
        }
      >
        {ft.hero.paragraph1}
      </SectionHead>

      <div className="rail">
        <div className="rail__label" aria-hidden="true" />
        <div className="space-y-10">
          <AwardCard ft={ft} />
          <StatRow items={stats} />
          <Reveal>
            <FlowLine text={ft.hero.flow} />
          </Reveal>
        </div>
      </div>
    </Band>
  );
};

/* ---------- full fintech page ------------------------------------------ */

const Fintech = () => {
  const { t } = useLanguage();
  const ft = t.fintech;

  const progressRef = useRef(null);
  const progressInView = useInView(progressRef, { once: true, margin: "-80px" });
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (!progressInView) return undefined;
    const timer = setTimeout(() => setProgress(ft.journey.progressValue), 250);
    return () => clearTimeout(timer);
  }, [progressInView, ft.journey.progressValue]);

  const steps = [
    { icon: "database", title: ft.whatIs.step1Title, desc: ft.whatIs.step1Desc },
    { icon: "cpu", title: ft.whatIs.step2Title, desc: ft.whatIs.step2Desc },
    { icon: "bank", title: ft.whatIs.step3Title, desc: ft.whatIs.step3Desc },
    { icon: "coins", title: ft.whatIs.step4Title, desc: ft.whatIs.step4Desc },
  ];

  const criteria = [
    { icon: "trend", title: ft.whatIs.criteria1, desc: ft.whatIs.criteria1Desc },
    { icon: "pulse", title: ft.whatIs.criteria2, desc: ft.whatIs.criteria2Desc },
    { icon: "clipboard", title: ft.whatIs.criteria3, desc: ft.whatIs.criteria3Desc },
    { icon: "scale", title: ft.whatIs.criteria4, desc: ft.whatIs.criteria4Desc },
  ];

  const market = [
    { label: ft.market.tamLabel, value: ft.market.tamValue, desc: ft.market.tamDesc, share: 100 },
    { label: ft.market.samLabel, value: ft.market.samValue, desc: ft.market.samDesc, share: 15 },
    { label: ft.market.somLabel, value: ft.market.somValue, desc: ft.market.somDesc, share: 2.2 },
  ];

  const problemStats = [
    { value: ft.problem.stat1Value, label: ft.problem.stat1Label, desc: ft.problem.stat1Desc },
    { value: ft.problem.stat2Value, label: ft.problem.stat2Label, desc: ft.problem.stat2Desc },
    { value: ft.problem.stat3Value, label: ft.problem.stat3Label, desc: ft.problem.stat3Desc },
    { value: ft.problem.stat4Value, label: ft.problem.stat4Label, desc: ft.problem.stat4Desc },
  ];

  return (
    <div id="fintech">
      {/* ---------- page header ---------------------------------------- */}
      <section className="band band--flush pt-14 md:pt-20">
        <div className="shell">
          <Reveal>
            <span className="eyebrow">COSEBU · {t.nav.fintech}</span>
          </Reveal>
          <Reveal delay={0.04}>
            <h1 className="display mt-5 max-w-[18ch]">{ft.hero.title}</h1>
          </Reveal>

          <div className="mt-12 grid gap-12 lg:grid-cols-12 lg:gap-16">
            <div className="lg:col-span-7">
              <Reveal delay={0.08} className="space-y-5">
                <p className="subhead">{ft.hero.paragraph1}</p>
                <p className="title-sm text-clay">{ft.hero.paragraph2}</p>
                <p className="subhead">{ft.hero.paragraph3}</p>
                <p className="subhead">{ft.hero.paragraph4}</p>
              </Reveal>

              <Reveal delay={0.12} className="mt-9">
                <p className="eyebrow mb-3">{ft.hero.howItWorks}</p>
                <FlowLine text={ft.hero.flow} />
              </Reveal>
            </div>

            <Reveal delay={0.16} className="lg:col-span-5">
              <div className="panel h-full p-7 md:p-8">
                <span className="eyebrow">{ft.hero.mission}</span>
                <p className="title-sm mt-4">{ft.hero.missionText}</p>
                <a href={PARTNER_EMAIL} className="btn btn--primary mt-8">
                  {ft.cta.button}
                  <Icon name="arrowUpRight" size={16} />
                </a>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ---------- award ------------------------------------------------ */}
      <Band>
        <SectionHead index={1} label={ft.award.badge} title={ft.award.title} />
        <div className="rail">
          <div className="rail__label" aria-hidden="true" />
          <AwardCard ft={ft} />
        </div>
      </Band>

      {/* ---------- journey ---------------------------------------------- */}
      <Band tint>
        <SectionHead index={2} label={t.nav.fintech} title={ft.journey.title} />

        <div className="rail">
          <div className="rail__label" aria-hidden="true" />
          <div className="grid gap-8 md:grid-cols-2">
            <Reveal className="panel flex flex-col p-7 md:p-8">
              <span className="tag tag--live">
                <span className="dot" />
                {ft.journey.phase1Status}
              </span>
              <h3 className="title-sm mt-5">{ft.journey.phase1Title}</h3>
              <p className="meta mt-3">{ft.journey.phase1Desc}</p>

              <div className="mt-7 grid grid-cols-2 gap-6 border-t border-line pt-6">
                <div>
                  <p className="num text-[28px] leading-none">{ft.journey.businesses}</p>
                  <p className="meta mt-2 text-[13px]">{ft.journey.businessesLabel}</p>
                </div>
                <div className="border-l border-line pl-6">
                  <p className="num text-[28px] leading-none">{ft.journey.volume}</p>
                  <p className="meta mt-2 text-[13px]">{ft.journey.volumeLabel}</p>
                </div>
              </div>

              <div ref={progressRef} className="mt-auto pt-8">
                <div className="flex items-baseline justify-between">
                  <span className="meta text-[13px]">{ft.journey.progressLabel}</span>
                  <span className="num text-[13px]">{progress}%</span>
                </div>
                <div
                  className="mt-2.5 h-1.5 w-full overflow-hidden rounded-full bg-surface-2"
                  role="progressbar"
                  aria-valuenow={progress}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-label={ft.journey.progressLabel}
                >
                  <div
                    className="h-full rounded-full bg-accent transition-[width] duration-[900ms] ease-out"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.06} className="panel flex flex-col p-7 md:p-8">
              <span className="tag tag--next">
                <span className="dot" />
                {ft.journey.phase2Status}
              </span>
              <h3 className="title-sm mt-5">{ft.journey.phase2Title}</h3>
              <p className="meta mt-3">{ft.journey.phase2Desc}</p>

              <ol className="mt-auto divide-rule border-t border-line pt-2">
                {steps.map((step, index) => (
                  <li key={step.title} className="flex items-center gap-3.5 py-3.5">
                    <span className="num w-5 flex-none text-[12px] text-ink-3">
                      {String(index + 1).padStart(2, "0")}
                    </span>
                    <Icon name={step.icon} size={17} className="flex-none text-accent" />
                    <span className="text-[14px]">{step.title}</span>
                  </li>
                ))}
              </ol>
            </Reveal>
          </div>
        </div>
      </Band>

      {/* ---------- problem ---------------------------------------------- */}
      <Band>
        <SectionHead index={3} label={t.home.problemLabel} title={ft.problem.title} />
        <div className="rail">
          <div className="rail__label" aria-hidden="true" />
          <StatRow items={problemStats} />
        </div>
      </Band>

      {/* ---------- how it works ------------------------------------------ */}
      <Band tint>
        <SectionHead index={4} label={ft.whatIs.subtitle} title={ft.whatIs.title}>
          {ft.whatIs.flowSummary}
        </SectionHead>

        <div className="rail">
          <div className="rail__label" aria-hidden="true" />

          <div className="grid gap-x-10 border-t border-line md:grid-cols-2 lg:grid-cols-4">
            {steps.map((step, index) => (
              <Reveal
                key={step.title}
                delay={index * 0.05}
                className="border-b border-line py-7 lg:border-b-0 lg:border-r lg:pr-8 lg:last:border-r-0"
              >
                <div className="flex items-center gap-3">
                  <span className="num text-[12px] text-ink-3">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <span className="h-px flex-1 bg-line" />
                  <Icon name={step.icon} size={18} className="text-accent" />
                </div>
                <h3 className="mt-5 text-[16px] font-semibold">{step.title}</h3>
                <p className="meta mt-2 text-[13px]">{step.desc}</p>
              </Reveal>
            ))}
          </div>

          <div className="mt-14">
            <Reveal>
              <h3 className="title-sm">{ft.whatIs.scoringTitle}</h3>
            </Reveal>

            <div className="mt-6 grid gap-x-10 border-t border-line sm:grid-cols-2">
              {criteria.map((item, index) => (
                <Reveal
                  key={item.title}
                  delay={index * 0.04}
                  className="flex gap-4 border-b border-line py-6"
                >
                  <Icon name={item.icon} size={19} className="mt-0.5 flex-none text-clay" />
                  <div>
                    <h4 className="text-[15px] font-semibold">{item.title}</h4>
                    <p className="meta mt-1.5 text-[13px]">{item.desc}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </Band>

      {/* ---------- market size -------------------------------------------- */}
      <Band>
        <SectionHead index={5} label={t.home.directionLabel} title={ft.market.title} />

        <div className="rail">
          <div className="rail__label" aria-hidden="true" />
          <div className="divide-rule border-y border-line">
            {market.map((tier, index) => (
              <Reveal key={tier.label} delay={index * 0.06} className="py-7">
                <div className="flex flex-wrap items-baseline gap-x-4 gap-y-2">
                  <span className="tag tag--accent">{tier.label}</span>
                  <span className="num text-[26px] leading-none">{tier.value}</span>
                  <span className="num ml-auto text-[12px] text-ink-3">
                    {tier.share}%
                  </span>
                </div>
                <div className="mt-4 h-1.5 w-full rounded-full bg-surface-2">
                  <div
                    className="h-full rounded-full bg-accent"
                    style={{ width: `${Math.max(tier.share, 2)}%` }}
                  />
                </div>
                <p className="meta mt-3 max-w-[64ch] text-[13px]">{tier.desc}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </Band>

      {/* ---------- demo ---------------------------------------------------- */}
      <Band tint>
        <SectionHead index={6} label={t.home.demoLabel} title={ft.demo.title} />
        <div className="rail">
          <div className="rail__label" aria-hidden="true" />
          <Reveal>
            <VideoFrame id={DEMO_VIDEO} title={ft.demo.title} />
          </Reveal>
        </div>
      </Band>

      {/* ---------- closing CTA ---------------------------------------------- */}
      <Band>
        <div className="grid gap-10 lg:grid-cols-12 lg:items-end">
          <Reveal className="lg:col-span-7">
            <h2 className="title max-w-[16ch]">{ft.cta.title}</h2>
            <p className="subhead mt-5 max-w-prose">{ft.cta.description}</p>
          </Reveal>
          <Reveal delay={0.06} className="lg:col-span-5 lg:text-right">
            <a href={PARTNER_EMAIL} className="btn btn--primary btn--lg">
              {ft.cta.button}
              <Icon name="arrowUpRight" size={16} />
            </a>
          </Reveal>
        </div>
      </Band>
    </div>
  );
};

export default Fintech;
